<template>
  <div class="table-container">
    <div v-if="!tableValues.length" class="no-result show-medium">{{ $t('noAlerts') }}</div>

    <table class="table hide-medium" border="0" cellspacing="0" cellpadding="0">
      <thead class="table-header">
        <tr>
          <th v-for="data in headers" :key="data" class="table-header-item" :class="{ right: data.includes('Lu/Non') }">
            {{ data }}
          </th>
        </tr>
      </thead>

      <tbody class="table-content">
        <tr v-for="alert in tableValues" :key="alert.publicId" class="table-line" @click="() => readDocument(alert)">
          <td class="table-item" :class="`${tableItemClass(alert)}`">
            <div class="item">{{ $utils.formatDate(alert.createdAt) }}</div>
          </td>

          <td
            v-if="config.headingFilters.denomination"
            class="table-item company-name"
            :class="`${tableItemClass(alert)}`"
            @click.stop="$router.push(`search/${alert.company.siren}`)"
          >
            <div class="item">{{ alert.company.name }}</div>
          </td>

          <td v-if="config.headingFilters.monitoringLevel" class="table-item" :class="`${tableItemClass(alert)}`">
            <MonitoringType :level="getMonitoringInfos(alert.packageLevelPublicId).level" :label="getMonitoringInfos(alert.packageLevelPublicId).label" />
          </td>

          <td class="table-item" :class="`${tableItemClass(alert)}`" :title="alert.document.typeDecisions.decisions">
            <div class="item document">
              <div class="document-title">{{ getDocumentValue(alert.document.type) }}</div>
              <div class="document-decision">{{ alert.document.typeDecisions.type }}</div>
              <div v-if="alert.document.typeDecisions && alert.document.type === 1" class="document-decision">
                <div v-for="decision in alert.document.typeDecisions.decisions" :key="decision" class="kbis-alerts">
                  {{ decision }}
                </div>
              </div>
            </div>
          </td>

          <td v-if="config.headingFilters.manager" class="table-item" :class="`${tableItemClass(alert)}`">
            <div class="item">
              {{ getManagerInfos(alert.managerPublicId).firstName }}
              {{ getManagerInfos(alert.managerPublicId).lastName }}
            </div>
          </td>

          <td class="table-item" :class="`${tableItemClass(alert)} read-indication`">
            <Toggle
              :loading="alertLoading === alert.publicId && loading"
              :value="alert.isRead"
              :values="['Lu', 'Non lu']"
              :on-change="() => toggleAlert(alert)"
              :date="alert.readAt"
            />
          </td>
        </tr>
        <tr v-if="!tableValues.length" class="table-line disabled">
          <td class="table-item" colspan="7" :class="{ read: true }">
            <div class="item center">{{ $t('noAlerts') }}</div>
          </td>
        </tr>
      </tbody>
    </table>
    <div class="table-container show-medium">
      <div v-for="alert in tableValues" :key="alert.id" class="table-line" :class="`${tableItemClass(alert)}`" @click="() => readDocument(alert)">
        <div class="line">
          <div class="line-content date">
            <div class="item">{{ $utils.formatDate(alert.createdAt) }}</div>
          </div>
          <div class="line-content monitoring">
            <Toggle
              :loading="alertLoading === alert.publicId && loading"
              :value="alert.isRead"
              :values="['Lu', 'Non lu']"
              :on-change="() => toggleAlert(alert)"
              :date="alert.readAt"
            />
          </div>
        </div>

        <div v-if="config.headingFilters.denomination" class="line">
          <div class="line-content label">
            <div class="item">{{ alert.company.name }}</div>
          </div>
        </div>

        <div v-if="config.headingFilters.monitoringLevel" class="line column">
          <div class="line-content title">{{ $t('monitoringLevel') }}</div>
          <div class="line-content">
            <MonitoringType :level="getMonitoringInfos(alert.packageLevelPublicId).level" :label="getMonitoringInfos(alert.packageLevelPublicId).label" />
          </div>
        </div>

        <div class="line column">
          <div class="line-content title">{{ $t('concernedDocuments') }}</div>
          <div class="line-content">
            <div class="item">{{ getDocumentValue(alert.document.type) }}</div>
          </div>
        </div>

        <div v-if="config.headingFilters.manager" class="line column">
          <div class="line-content title">{{ $t('manager') }}</div>
          <div class="line-content">
            <div class="item">
              {{ getManagerInfos(alert.managerPublicId).firstName }}
              {{ getManagerInfos(alert.managerPublicId).lastName }}
            </div>
          </div>
        </div>

        <!-- <div class="line right">
          <div class="line-content read-indication" :class="`${tableItemClass(alert)}`">
            <div class="item">{{alert.read ? $t('read') : $t('unread')}}</div>
          </div>
        </div>-->
      </div>
    </div>
    <Pagination
      v-if="tableValues && tableValues.length && config.pagination && loaded"
      :pagination="pagination"
      :set-page="setPage"
      :tableValues="tableValues"
      :pages="pages"
      :disable-dots="disableDots"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import MonitoringType from '@/components/monitoringType';
import Pagination from '@/components/Pagination';
import tableMixin from '@/components/tables/TableMixin';
import Toggle from '@/components/Toggle';

export default {
  components: { Pagination, Toggle, MonitoringType },
  mixins: [tableMixin],

  props: {
    packages: {
      type: Array,
      required: true,
    },

    users: {
      type: Array,
      required: true,
    },

    filters: {
      type: Object,
      required: true,
    },

    config: {
      type: Object,
      required: false,

      default: () => ({
        pagination: false,
        internalUserOnly: false,
        companySiren: false,
        headingFilters: {
          denomination: true,
          monitoringLevel: true,
          event: true,
          manager: true,
        },
      }),
    },
  },

  data() {
    return {
      headers: [
        this.$t('alertDate'),
        this.$t('companyLabel'),
        this.$t('monitoringLevel'),
        this.$t('concernedDocuments'),
        this.$t('manager'),
        this.$t('readUnread'),
      ],
      values: [],
      tableMixinConfig: {
        backendPaginated: true,
        onChange: this.changePage,
        getData: this.getData,
        maxElement: this.config.maxElement || 10,
      },
      loaded: false,
      documentsValues: [
        { type: this.$t('documentTypes.kbis'), value: 1 },
        { type: this.$t('documentTypes.actes'), value: 2 },
        { type: this.$t('documentTypes.bilan'), value: 4 },
        { type: this.$t('documentTypes.beneficiary'), value: 8 },
      ],
      loading: false,
      alertLoading: '',
    };
  },

  computed: {
    ...mapGetters(['isLogged', 'user']),
  },

  watch: {
    async 'filters.manager'(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.setPage(0);
      }
    },

    async 'filters.monitoring'(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.setPage(0);
      }
    },

    async 'filters.search'(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.setPage(0);
      }
    },

    async 'filters.isRead'(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.setPage(0);
      }
    },

    async 'filters.year'(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.setPage(0);
      }
    },

    async 'filters.paginationNumber'(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.tableMixinConfig.maxElement = newValue;
        this.pagination.maxElement = newValue;
        this.setPage(0);
      }
    },
  },

  async mounted() {
    if (!this.config.headingFilters.denomination) {
      this.headers = this.headers.filter(e => e !== this.$t('companyLabel'));
    }
    if (!this.config.headingFilters.monitoringLevel) {
      this.headers = this.headers.filter(e => e !== this.$t('monitoringLevel'));
    }
    if (!this.config.headingFilters.event) {
      this.headers = this.headers.filter(e => e !== this.$t('event'));
    }
    if (!this.config.headingFilters.manager) {
      this.headers = this.headers.filter(e => e !== this.$t('manager'));
    }
    this.loaded = true;
  },

  methods: {
    async readDocument(alert) {
      const result = await this.$store.dispatch('getCompanyPdf', {
        publicId: alert.document.publicId,
      });
      window.open(result.url, '_blank');
      if (!alert.isRead) this.toggleAlert(alert);
    },

    async toggleAlert(alert) {
      this.alertLoading = alert.publicId;
      const result = await this.$store.dispatch('toggleAlertRead', {
        id: alert.publicId,
      });
      alert.isRead = !alert.isRead;
      alert.readAt = new Date().toString();
      this.alertLoading = '';
      return result;
    },

    getDocumentValue(value) {
      return this.documentsValues.find(e => e.value === value).type;
    },

    goTo({ siren }) {
      this.$router.push(`search/${siren}`);
    },

    getMonitoringInfos(packageId) {
      const info = this.packages.find(value => value.publicId === packageId);
      return info;
    },

    getManagerInfos(managerId) {
      const info = this.users.find(value => value.id === managerId);
      return info;
    },

    tableItemClass(alert) {
      let result = '';
      if (alert.isRead) result += 'read ';
      return result;
    },

    async getData(pageNumber = 0, pageSize = this.tableMixinConfig.maxElement) {
      let data;
      if (this.config.companySiren) {
        if (!this.filters.isFiltered) {
          data = await this.$store.dispatch('getAlerts', {
            pageNumber,
            pageSize,
            search: this.config.companySiren,
          });
        } else {
          data = await this.$store.dispatch('getAlerts', {
            pageNumber,
            pageSize,
            search: this.config.companySiren,
            monitoring: this.filters.monitoring,
            isRead: this.filters.isRead,
            year: this.filters.year,
          });
        }
      } else if (this.config.internalUserOnly) {
        if (!this.filters.isFiltered) {
          data = await this.$store.dispatch('getAlerts', {
            pageNumber,
            pageSize,
            manager: this.user.id,
          });
        } else {
          data = await this.$store.dispatch('getAlerts', {
            pageNumber,
            pageSize,
            manager: this.user.id,
            monitoring: this.filters.monitoring,
            search: this.filters.search,
            isRead: this.filters.isRead,
            year: this.filters.year,
          });
        }
      } else {
        if (!this.filters.isFiltered) {
          data = await this.$store.dispatch('getAlerts', {
            pageNumber,
            pageSize,
          });
        } else {
          data = await this.$store.dispatch('getAlerts', {
            pageNumber,
            pageSize,
            manager: this.filters.manager,
            monitoring: this.filters.monitoring,
            search: this.filters.search,
            isRead: this.filters.isRead,
            year: this.filters.year,
          });
        }
      }
      this.values = data.items;
      this.pagination.totalLength = data.count;
      this.pagination.maxPage = Math.ceil(this.pagination.totalLength / this.pagination.maxElement);
      return { data, values: data.items };
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';
.table .table-header .table-header-item {
  &.right {
    text-align: right;
  }
}

.table .table-content .table-line,
.table-container .table-line {
  &:not(.disabled) {
    &:hover {
      cursor: pointer;

      .table-item {
        background: rgba(colors.$unread-table-color, 0.6);
      }
    }
  }
}

.kbis-alerts {
  &::before {
    content: '-';
    margin-right: 4px;
  }
}

.table-item.company-name:hover {
  .item {
    transition: 0.3s ease all;
    text-decoration: underline;
  }
}

.table .table-content .table-line .table-item,
.table-container .table-line .line .line-content {
  .item {
    &.document {
      flex-direction: column;
      align-items: self-start;

      .document-title {
        width: 100%;
        text-align: left;
      }

      .document-decision {
        opacity: 0.6;
        font-weight: initial;
      }
    }

    &.center {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &.monitoring {
    display: flex;
    align-items: center;

    .item {
      &:nth-child(2n) {
        color: colors.$red-color;
        font-size: 14px;
      }

      &:not(:nth-child(2n)) {
        flex-shrink: 0;
        width: 17px;
        height: 17px;
        background: colors.$red-color;
        color: colors.$white-color;
        border-radius: 50%;
        font-size: 12px;
        margin: 0 8px 0 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }

  &.edit {
    .item {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 4px 8px;
      font-size: 12px;
      background: colors.$grey-color;
      border-radius: 24px;
      color: colors.$white-color;
    }
  }

  &.read-indication {
    .toggle-container {
      margin: 0 0 0 auto;
    }

    .item {
      position: relative;
      padding-left: 12px;

      &:nth-child(2n + 1):before {
        position: absolute;
        left: 0px;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        content: '';
        display: flex;
        align-items: center;
        justify-content: center;
        background: colors.$green-color;
      }
    }

    &.read .item:nth-child(2n + 1):before {
      width: 5px;
      height: 5px;
      border: 1px solid colors.$grey-color;
      background: colors.$read-table-color;
    }
  }
}

@media only screen and (max-width: responsive.$layout-breakpoint-medium) {
  .no-result {
    text-align: center;
    height: 256px;
    justify-content: center;
    align-items: center;
    display: flex;

    color: colors.$lightgrey-color;
    font-weight: 300;
  }

  .table-container {
    width: 100%;

    .table-line {
      width: 100%;
      // height: 192px;
      margin: 12px 0;
      display: flex;
      flex-direction: column;

      margin-bottom: 0;

      background: colors.$unread-table-color;
      padding: 20px;
      box-sizing: border-box;
      border-radius: 4px;

      &.read {
        background: rgba(colors.$lightgrey-color, 0.2);

        .item {
          opacity: 0.7;
        }
      }

      .line {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-grow: 1;

        margin: 12px 0;

        &.right {
          justify-content: flex-end;
        }

        &.column {
          flex-direction: column;
          justify-content: space-evenly;
          align-items: flex-start;
        }

        .line-content {
          display: flex;
          align-items: center;
          justify-content: space-between;

          .item {
            display: flex;
            align-items: center;
          }

          &.read-indication {
            align-self: flex-end;
          }

          &.title {
            font-weight: 500;
            color: colors.$grey-color;
            line-height: 16px;
            font-size: 14px;
            margin-bottom: 8px;
          }

          &.label,
          &.date {
            font-weight: 500;
            color: colors.$black-color;
          }

          &.date,
          .internal-id {
            font-size: 12px;
          }

          &.label {
            font-size: 16px;
          }
        }
      }
    }
  }
}
</style>
